import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { accentFor } from "../lib/accents";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

/**
 * Reusable admin edit dialog for a single ad.
 * Used on the Admin Ads & Ordering page AND the client library wall (admin-only affordance).
 * - `ad`: the ad object being edited (or null to close)
 * - `categories`: category list for tag chips
 * - `onClose`: called after cancel or successful save
 * - `onSaved`: called with the updated ad after a successful save
 */
export function AdEditModal({ ad, categories, onClose, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", hook: "", angle: "", offer: "", category_ids: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ad) return;
    setForm({
      title: ad.title || "",
      description: ad.description || "",
      hook: ad.hook || "",
      angle: ad.angle || "",
      offer: ad.offer || "",
      category_ids: ad.category_ids || [],
    });
  }, [ad]);

  const toggleCat = (id) =>
    setForm((f) => ({ ...f, category_ids: f.category_ids.includes(id) ? f.category_ids.filter((c) => c !== id) : [...f.category_ids, id] }));

  const save = async () => {
    if (!ad) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/ads/${ad.id}`, form);
      toast.success("Ad updated");
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!ad} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent data-testid="edit-ad-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Edit ad</DialogTitle>
          <DialogDescription className="text-xs text-gray-500">Update title, description, hook, angle, offer, or categories.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Title</label>
            <input data-testid="edit-ad-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Description</label>
            <textarea data-testid="edit-ad-description-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {[
              { key: "hook", label: "Hook", ph: "e.g. 84% of CEOs have a mentor" },
              { key: "angle", label: "Angle", ph: "e.g. authority / social proof" },
              { key: "offer", label: "Offer", ph: "e.g. Free 7-day trial" },
            ].map((f) => (
              <div key={f.key}>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">{f.label}</label>
                <input
                  data-testid={`edit-ad-${f.key}-input`}
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.ph}
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0FFFE2]"
                />
              </div>
            ))}
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Categories</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {categories.map((c) => {
                const active = form.category_ids.includes(c.id);
                const a = accentFor(c.color);
                return (
                  <button key={c.id} data-testid={`edit-cat-chip-${c.slug}`} onClick={() => toggleCat(c.id)}
                    className="px-3 py-1 rounded-full text-xs font-bold transition-colors"
                    style={active ? { background: a.hex, color: a.text } : { background: "#F3F4F6", color: "#000000" }}>
                    {c.name}
                  </button>
                );
              })}
            </div>
          </div>
          <button data-testid="save-ad-btn" onClick={save} disabled={saving}
            className="w-full bg-[#000000] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#4A0A66] transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
